import { useNuxtApp } from '#app'

import type { StorageLike } from '@packages/persiststate/types/storage.ts'
import type { LocalStorageExpirationOptions } from '@packages/persiststate/types/storages/local-storage.ts'

function usePersistedstateLocalStorage (): StorageLike {
  return {
    getItem: (key) => {
      if (!useNuxtApp().ssrContext) {
        const itemValue: any = tryGetLocalStorage(key)
        return itemValue
      }
    },
    setItem: (key, value) => {
      if (!useNuxtApp().ssrContext) {
        trySetLocalStorage(key, value)
      }
    },
  }
}

function usePersistedstateLocalStorageWithExpiration (expirationOptions: LocalStorageExpirationOptions): StorageLike {
  const { renewOnUpdate, expiration } = {
    renewOnUpdate: true,
    ...expirationOptions,
  }

  const expirationStorageKeySuffix = 'exp'

  const isExpired = (expirationStorageKey: string) => {
    const expirationTime = tryGetLocalStorage(expirationStorageKey)
    return typeof expirationTime !== 'string' || parseInt(expirationTime) < Date.now()
  }

  return {
    getItem: (key) => {
      try {
        if (useNuxtApp().ssrContext || !window.localStorage) {
          return null
        }
      } catch {
        return null
      }

      const expirationStorageKey = `${key}_${expirationStorageKeySuffix}`

      if (!isExpired(expirationStorageKey)) {
        return tryGetLocalStorage(key)
      }

      tryRemoveLocalStorage(key)
      tryRemoveLocalStorage(expirationStorageKey)

      return null
    },
    setItem: (key, value) => {
      try {
        if (useNuxtApp().ssrContext || !window.localStorage) {
          return
        }
      } catch {
        return
      }

      const expirationStorageKey = `${key}_${expirationStorageKeySuffix}`

      if (renewOnUpdate || tryGetLocalStorage(expirationStorageKey) === null) {
        trySetLocalStorage(
          expirationStorageKey,
          (Date.now() + expiration).toString()
        )
      }

      if (isExpired(expirationStorageKey)) {
        tryRemoveLocalStorage(key)
      } else {
        trySetLocalStorage(key, value)
      }
    },
  }
}

export const localPersistedState = {
  localStorage: usePersistedstateLocalStorage(),
  localStorageWithExpiration: usePersistedstateLocalStorageWithExpiration,
}
